import { animate, AnimationEvent, state, style, transition, trigger } from '@angular/animations';
import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, Inject, InjectionToken, Injector, OnDestroy, OnInit, Renderer2, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ButtonDirective } from '@coreui/angular-pro';
import { IconDirective } from '@coreui/icons-angular';
import { ScreenOrientation } from "@ionic-native/screen-orientation/ngx";
import { StatusBar } from "@ionic-native/status-bar/ngx";
import { Logger, Platform, SoundService } from 'shared';
import { Media } from '../../models/media';
import { MediaViewerComponent } from '../media-viewer/media-viewer.component';
import { MediaViewerOverlayRef } from "./media-viewer-overlay-ref";

export interface MediaViewerConfig {
  media: Media,
  context?: any,
  injector?: Injector;
}

export const MEDIA_VIEWER_CONFIG = new InjectionToken<any>('MEDIA_VIEWER_CONFIG');

declare type ActionId = 'open' | 'close' | 'destroy';

class Action {

  constructor(public id: ActionId, 
    public complete: (result?: any) => void, 
    public error: (error: string) => void) {}

  matchesState(state: 'closed' | 'open'): boolean {
    return state=='closed' 
      ? this.id == 'close' || this.id == 'destroy' 
      : this.id == 'open';
  }
}

@Component({
    selector: 'media-viewer-overlay',
    template: `
      <div [@slideInOut]="state()" 
          (@slideInOut.start)="onAnimationStart($event)" 
          (@slideInOut.done)="onAnimationDone($event)"
          class="overlay" 
          [ngClass]="config.media.type">
        <media-viewer></media-viewer>
        @let isConference = config.media.type == 'conference';
        <div class="toolbar" [class.toolbar-conference]="isConference">
          @if (isConference) {
            <div class="close" (click)="overlayRef.close()" >
              <svg aria-label="Close" cIcon name="arrowDown"></svg>
            </div>
          }
          @else {
            <button cButton color="primary" (click)="overlayRef.destroy()">Close</button>
          }
        </div>
      </div>
   `,
    styles: [`
      .overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        margin: 0;
        border-radius: 0;
        background-color: #fff;
        box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.15);
        transform: translateY(100%); /* Start off-screen at bottom */
        z-index: 1000;
      }
      .overlay.pdf, .overlay.eap {
        padding-top: env(safe-area-inset-top);
      }
      media-viewer {
        height: 100%;
      }
      .toolbar {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        /* 
          videojs control bar 
          background-color: rgba(43, 51, 63, 0.7); 
        */
        background-color: transparent;
        padding: 5px;
        .close {
          padding: 3px 14px 0 14px;
          border-radius: 3px 3px 0 0;
          border: 1px solid #08a5c0;
          border-bottom: none;
          background-color: #0880c0;
          cursor: pointer;
          svg {
            fill: white;
            display: block;
          }
        }
      }
      .toolbar-conference {
        padding: 0px;
      }
  `],
  imports: [MediaViewerComponent, NgClass, ButtonDirective, IconDirective],
  animations: [
    trigger('slideInOut', [
      state('open', style({transform: 'translateY(0)' })),
      state('closed', style({transform: 'translateY(100%)' })),
      transition('closed => open', animate('400ms ease-in')),
      transition('open => closed', animate('400ms ease-out'))
    ]),
    // trigger('slideInOutToolbar', [
    //   state('in', style({ transform: 'translate(-50%, -50%)', opacity: 1 })),
    //   state('out', style({ transform: 'translate(-50%, 100%)',opacity: 0 })),
    //   transition('void => in', [
    //     style({ transform: 'translate(-50%, 100%)', opacity: 0 }),
    //     animate('400ms ease-in', style({ transform: 'translate(-50%, -50%)', opacity: 1 }))
    //   ]),
    //   transition('in => out', [
    //     animate('400ms ease-out', style({ transform: 'translate(-50%, 100%)', opacity: 0 }))
    //   ])
    // ])
  ],
  changeDetection: ChangeDetectionStrategy.Default
})
export class MediaViewerOverlayComponent implements OnInit, OnDestroy {

  Media = Media;

  @ViewChild(MediaViewerComponent, { static: true }) protected _viewer!: MediaViewerComponent;
  get viewer(): MediaViewerComponent {
    return this._viewer;
  }

  // plain state property does not work with zoneless CD 
  // because the state value is updated using Promise. 
  // Zone.js monkey-patches Promise, setTimeout, fetch, etc.,
  // state: 'open' | 'closed' = 'closed';  
  state = signal< 'open' | 'closed'>('closed');

  protected action: Action | undefined = undefined;
  protected screenOrientationType: string | null = null;

  private readonly TOGGLE_PATH = 'M12 6.586 L3.293 15.293 L4.707 16.707 L12 9.414 L19.293 16.707 L20.707 15.293 Z';
  private readonly HANGUP_PATH = 'M488.389,243.6l-14.783-14.78a307.74,307.74,0,0,0-435.212,0h0L23.608,243.607a24.5,24.5,0,0,0-6.582,22.954l.182.788,29.355,86.368,145.882-20.84,9.538-66.77a81.8,81.8,0,0,1,108.034,0l9.538,66.77,145.882,20.84,29.094-85.6.261-.764.182-.788A24.5,24.5,0,0,0,488.389,243.6Zm-44.707,74.68L347.84,304.592l-7.691-53.831-3.771-3.772a113.8,113.8,0,0,0-160.756,0l-3.771,3.772-7.691,53.831L68.318,318.284l-18.8-55.326,11.507-11.506h0a275.741,275.741,0,0,1,389.958,0l11.507,11.506Z';

  protected logger = new Logger(`MediaViewerOverlayComponent [mediaId=${this.config.media?.id}`);

  constructor(public overlayRef: MediaViewerOverlayRef,
              @Inject(MEDIA_VIEWER_CONFIG)
              public config: MediaViewerConfig,
              protected destroyRef: DestroyRef,
              protected elementRef: ElementRef,
              protected renderer: Renderer2,
              protected platform: Platform,
              private screenOrientation: ScreenOrientation,
              private statusBar: StatusBar,
              private soundService: SoundService) {
  }

  ngOnInit(): void {
    this.logger.debug('ngOnInit');

    this.overlayRef.onClose.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((callback) => {
        this.close().then(() => {
          callback?.()
        });
    });

    this.overlayRef.onOpen.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((callback) => {
      this.open().then(() => {
        callback?.();
      });
    });

    this.overlayRef.onDestroy.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((callback) => {
      this.destroy().then(() => {
        try {
          callback?.();
        } finally {
          // let body = document.querySelector('.body') as HTMLElement;
          // if (body) {
          //   const statusbar = document.querySelector('.statusbar') as HTMLElement;
          //   if (statusbar) {
          //     this.renderer.removeChild(body, statusbar);
          //   }
          // }
        }
      });
    });

    this.overlayRef.onDestroyed.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((callback) => {
      let body = document.querySelector('.body') as HTMLElement;
      if (body) {
        const statusbar = document.querySelector('.statusbar') as HTMLElement;
        if (statusbar) {
          this.renderer.removeChild(body, statusbar);
          this.soundService.hangup();
        }
      }
    });

    this.viewer.onAction.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(action => {
      if (action=='close') {
        this.overlayRef.destroy();
      } else {
        this.overlayRef.onAction.emit({ action, parameter: this.viewer.media });
      }
    });

    if (this.platform.is('hybrid')) {
      this.screenOrientationType = this.screenOrientation.type;
      // this.statusBar.hide();
      if (!this.platform.is('tablet')) {
        this.screenOrientation.unlock();
      }
    }

    setTimeout(() => (this.overlayRef.open()), 100);
  }

  ngOnDestroy() {
    this.logger.debug('ngOnDestroy');
    if (this.platform.is('hybrid')) {
      // this.statusBar.show();
      // if (getCurrentThemeMode()=='dark' || !isThemeModePure()) {
      //   this.statusBar.styleBlackOpaque();
      // } else {
      //   this.statusBar.styleLightContent();
      // }
      if (this.screenOrientationType && !this.platform.is('tablet')) {
        this.screenOrientation
          .lock(this.screenOrientationType)
          .catch((error) => this.logger.error(`Failed to lock screen orientation to ${this.screenOrientationType}`, error));
      }
    }
  }

  protected destroy(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (this.state()=='closed') {
        resolve();
      } else {
        this.action = new Action('destroy', resolve, reject);
        this.state.set('closed');
      }
    });
  }

  protected close(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.state()=='closed') {
        resolve();
      } else {
        this.action = new Action('close', resolve, reject);
        this.state.set('closed');
      }
    });
  }

  protected open(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.state()=='open') {
        resolve()
      } else {
        this.action = new Action('open', resolve, reject);
        this.state.set('open');
      }
    });
  }
  
  protected update() {
    if (this.state()=='open') {
        // let body = document.querySelector('.body');
        // let statusbar = document.querySelector('.statusbar');
        // if (statusbar) {
          // this.renderer.removeChild(body, statusbar);
          // this.elementRef.nativeElement.style.display = '';
        // }

        if (this.platform.is('hybrid') && 
            this.statusBar.isVisible && 
            (this.viewer.media?.type=='video' || 
            this.viewer.media?.type=='image')
            ) {
          this.statusBar.hide();
        }
    } else {
      let body = document.querySelector('.body') as HTMLElement;
      if (body) {
        const currentStatusbar = document.querySelector('.statusbar') as HTMLElement;
        if (currentStatusbar) {
          const statusMediaId = currentStatusbar.dataset['mediaId'] || null;
          if (statusMediaId!=this.config.media?.id && this.action?.id != 'close') {
            // media viewer for media not displayed in status bar is closed
            return;
          } else {
            //close of any media or destroy of the same media - clean status bar
            this.renderer.removeChild(body, currentStatusbar);
          }
        } 
        if (this.action?.id == 'close') { // no destroy just close
          const width = body.offsetWidth;
          const dense = width < 600;

          // Statusbar
          const statusbar = this.renderer.createElement('div');
          statusbar.dataset['mediaId'] = this.config.media?.id;
          this.renderer.addClass(statusbar, 'statusbar');

          // Text
          const textElement = this.renderer.createElement('span');
          this.renderer.addClass(textElement, 'status-text');
          if (dense) {
            this.renderer.addClass(textElement, 'dense');
          }
          const text = this.renderer.createText(`${!dense ? 'Conference:' : ''}${this.config.media?.name}`);
          this.renderer.appendChild(textElement, text);
          this.renderer.appendChild(statusbar, textElement);

          const createSvgIcon = (
            options: {
              viewBox: string;
              path: string;
              size?: number;
              fill?: string;
              className?: string;
              ariaLabel?: string;
              role?: 'img' | 'button';
              tabIndex?: number;
              onClick?: (event: Event) => void;
            }
          ): SVGElement => {
          
            const svg = this.renderer.createElement('svg', 'http://www.w3.org/2000/svg');
            this.renderer.setStyle(svg, 'pointer-events', 'auto');
          
            this.renderer.setAttribute(svg, 'viewBox', options.viewBox);
            this.renderer.setAttribute(svg, 'width', String(options.size ?? 24));
            this.renderer.setAttribute(svg, 'height', String(options.size ?? 24));
          
            if (options.className) {
              this.renderer.addClass(svg, options.className);
            }
          
            // Accessibility
            this.renderer.setAttribute(svg, 'role', options.role ?? 'img');
            if (options.ariaLabel) {
              this.renderer.setAttribute(svg, 'aria-label', options.ariaLabel);
            }
            if (options.tabIndex !== undefined) {
              this.renderer.setAttribute(svg, 'tabindex', String(options.tabIndex));
            }
          
            const path = this.renderer.createElement('path', 'http://www.w3.org/2000/svg');
            this.renderer.setAttribute(path, 'd', options.path);
            this.renderer.setAttribute(path, 'fill', options.fill ?? 'currentColor');

            this.renderer.setStyle(path, 'pointer-events', 'none');
            
            this.renderer.appendChild(svg, path);

            if (options.onClick) {
              this.renderer.listen(svg, 'click', options.onClick);
              this.renderer.listen(svg, 'keydown', (e: KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  options.onClick!(e);
                }
              });
            }
          
            return svg;
          }

          // MINIMIZE ICON 
          // const minimizeSvg = this.renderer.createElement('svg', 'http://www.w3.org/2000/svg');
          // this.renderer.setAttribute(minimizeSvg, 'width', '24');
          // this.renderer.setAttribute(minimizeSvg, 'height', '24');
          // this.renderer.setAttribute(minimizeSvg, 'viewBox', '0 0 24 24');
          // this.renderer.addClass(minimizeSvg, 'status-icon');
          // const polygon = this.renderer.createElement('polygon', 'http://www.w3.org/2000/svg');
          // this.renderer.setAttribute(polygon, 'points', '12 6.586 3.293 15.293 4.707 16.707 12 9.414 19.293 16.707 20.707 15.293 12 6.586');
          // this.renderer.appendChild(minimizeSvg, polygon);
          // this.renderer.appendChild(statusbar, minimizeSvg);

          const minimizeSvg = createSvgIcon({
            viewBox: '0 0 24 24',
            path: this.TOGGLE_PATH,
            size: 24,
            className: 'status-icon',
            role: 'button',
            tabIndex: 0,
            ariaLabel: 'Open conference',
            // onClick: (event) => {
            //   this.overlayRef.open();
            // }
          });
          
          this.renderer.appendChild(statusbar, minimizeSvg);

          // HANGUP ICON
          const hangupIcon = createSvgIcon({
            viewBox: '0 0 512 512',
            path: this.HANGUP_PATH,
            size: 20,
            fill: 'white',
            className: 'hangup-icon',
            role: 'button',
            tabIndex: 0,
            ariaLabel: 'End conference',
            onClick: (event) => {
              event.preventDefault();
              event.stopPropagation();
              this.overlayRef.destroy();
            }
          });
          
          this.renderer.appendChild(statusbar, hangupIcon);
          this.renderer.appendChild(body, statusbar);
          this.renderer.listen(statusbar, 'click', () => {
            this.overlayRef.open();
          });
        }
      }
      if (this.platform.is('hybrid') && !this.statusBar.isVisible) {
        this.statusBar.show();
      }
    }
  }

  onAnimationStart(event: AnimationEvent): void {
    this.logger.debug('ANIMATION.START');
    if (event.toState === 'closed' && this.action?.matchesState('closed')) {
      this.update();
    } else if (event.toState === 'open' && this.action?.matchesState('open')) {
      this.elementRef.nativeElement.style.display = '';
    }
  }

  onAnimationDone(event: AnimationEvent): void {
    this.logger.debug('ANIMATION.DONE');
    if (event.toState === 'closed' && this.action?.matchesState('closed')) {
      this.elementRef.nativeElement.style.display = 'none';
      this.action?.complete?.();
    } else if (event.toState === 'open' && this.action?.matchesState('open')) {
      this.update();
      this.action?.complete?.();
    }
    this.action = undefined;
  }
}
